// Screen Wake Lock while playing: requested on play, released on pause and when the page is hidden
// (browsers release it themselves then), requested again when the page returns while still playing.
// Browsers without the API, and refusals, leave the screen to sleep; nothing else depends on it.

export class WakeLock {
  private sentinel: WakeLockSentinel | null = null;
  private wanted = false;

  constructor(private readonly changed: (held: boolean) => void = () => {}) {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) void this.release();
      else if (this.wanted) void this.acquire();
    });
  }

  get held(): boolean {
    return this.sentinel !== null;
  }

  /** true while playing, false on pause. */
  async hold(on: boolean): Promise<void> {
    this.wanted = on;
    if (on) await this.acquire();
    else await this.release();
  }

  private async acquire(): Promise<void> {
    if (this.sentinel || document.hidden || !('wakeLock' in navigator)) return;
    try {
      const sentinel = await navigator.wakeLock.request('screen');
      if (!this.wanted || document.hidden) { await sentinel.release().catch(() => {}); return; }
      sentinel.addEventListener('release', () => {
        if (this.sentinel === sentinel) { this.sentinel = null; this.changed(false); }
      });
      this.sentinel = sentinel;
      this.changed(true);
    } catch {
      /* refused (battery saver, permissions policy): the screen may sleep */
    }
  }

  private async release(): Promise<void> {
    const sentinel = this.sentinel;
    this.sentinel = null;
    if (!sentinel) return;
    this.changed(false);
    await sentinel.release().catch(() => {});
  }
}
